import React, { useEffect, useState } from "react"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material"

export type ReturnRequestItem = {
  product_id: number
  quantity: number
  reason: string
}

type Props = {
  order: any
  isSaving: boolean
  onClose: () => void
  onSubmit: (items: ReturnRequestItem[], notes: string) => void
}

const REASONS = ["Wrong size", "Damaged", "Wrong item", "Changed my mind"]

export const ReturnRequestDialog: React.FC<Props> = ({
  order,
  isSaving,
  onClose,
  onSubmit,
}) => {
  const [quantities, setQuantities] = useState<Record<number, number>>({})
  const [reasons, setReasons] = useState<Record<number, string>>({})
  const [notes, setNotes] = useState("")

  useEffect(() => {
    setQuantities({})
    setReasons({})
    setNotes("")
  }, [order?.id])

  const returnableItems = (order?.order_items ?? []).filter(
    (item: any) => Number(order.returnable_quantities?.[item.id] ?? 0) > 0
  )
  const selected = returnableItems
    .filter((item: any) => (quantities[item.id] ?? 0) > 0)
    .map((item: any) => ({
      product_id: item.id,
      quantity: quantities[item.id],
      reason: reasons[item.id] ?? "",
    }))

  return (
    <Dialog open={Boolean(order)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Request a return</DialogTitle>
      <DialogContent>
        <p className="text-sm text-gray-500 mb-4">
          Order #{order?.code}. Choose what you would like to return. The store
          will review your request.
        </p>
        <div className="space-y-4">
          {returnableItems.map((item: any) => {
            const max = Number(order.returnable_quantities[item.id])

            return (
              <div key={item.id} className="border-b border-gray-100 pb-4">
                <p className="text-sm font-medium text-gray-900 mb-2">
                  {item.name}
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <TextField
                    select
                    size="small"
                    label="Quantity"
                    value={quantities[item.id] ?? 0}
                    onChange={(event) =>
                      setQuantities({
                        ...quantities,
                        [item.id]: Number(event.target.value),
                      })
                    }
                    sx={{ minWidth: 110 }}
                  >
                    {Array.from({ length: max + 1 }, (_, quantity) => (
                      <MenuItem key={quantity} value={quantity}>
                        {quantity}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Reason"
                    fullWidth
                    value={reasons[item.id] ?? ""}
                    onChange={(event) =>
                      setReasons({ ...reasons, [item.id]: event.target.value })
                    }
                  >
                    {REASONS.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
              </div>
            )
          })}
        </div>
        <TextField
          label="Note (optional)"
          fullWidth
          multiline
          minRows={2}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          inputProps={{ maxLength: 1000 }}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          disabled={!selected.length || isSaving}
          onClick={() => onSubmit(selected, notes)}
        >
          Send Request
        </Button>
      </DialogActions>
    </Dialog>
  )
}
